import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import type { AnswerOut, ConselhoValidation } from '@/services/types';

type Headline = { key: string; check: string; statistic?: string; kind: 'numeric' | 'passfail' };

const ANATEL_HEADLINES: Headline[] = [
  { key: 'placebo', check: 'placebo_sigma_relator', statistic: 'p_value', kind: 'numeric' },
  { key: 'holdout', check: 'temporal_auc_lift', statistic: 'auc_lift', kind: 'numeric' },
  { key: 'split', check: 'split_tenure_r', statistic: 'pearson_r_first_vs_second_half', kind: 'numeric' },
  { key: 'sensitivity', check: 'ontology_sensitivity_rho', statistic: 'spearman_rho', kind: 'numeric' },
];

/** Headlines whose value has no meaningful sign: a p-value and a rank correlation. */
const UNSIGNED = new Set(['placebo', 'sensitivity']);

const CADE_HEADLINES: Headline[] = [
  { key: 'adjourned', check: 'no_adjourned_in_rollcall', kind: 'passfail' },
  { key: 'pairsReconcile', check: 'pairs_reconcile_with_rollcall', kind: 'passfail' },
  { key: 'metricsN', check: 'metrics_have_n', kind: 'passfail' },
  { key: 'rosterSlugs', check: 'roster_slugs_unique', kind: 'passfail' },
];

/**
 * The ETL's validation checks on the run the severity panel reads.
 *
 * ANATEL: four checks carry a headline each (placebo p-value, temporal AUC
 * lift, split-tenure r, and the case-versus-issue ontology sensitivity rho). CADE: four load-time artefact checks
 * (adjourned rows, pair reconciliation, metric n, unique slugs) — the
 * residual is a bootstrap, so a placebo/AUC card would describe a model
 * that was never fitted. Every other statistic is listed below them in the
 * generic form so a check added by the ETL appears without a code change.
 * A check the ETL could not compute arrives with a null value and its
 * reason in `note`, and is rendered as that absence rather than dropped.
 */
export function ValidationCard({
  validation,
  ns = 'anatel',
}: {
  validation: AnswerOut<ConselhoValidation[]>;
  ns?: string;
}) {
  const { t, i18n } = useTranslation([ns]);
  const number = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 3 });
  const signed = new Intl.NumberFormat(i18n.language, { signDisplay: 'exceptZero', maximumFractionDigits: 3 });
  const integer = new Intl.NumberFormat(i18n.language);
  const headlines = ns === 'cade' ? CADE_HEADLINES : ANATEL_HEADLINES;
  return (
    <div className="mt-4" data-testid="validation-card">
      <h3 className="mb-1 text-xs font-semibold text-foreground">{t(`${ns}:conselho.validation.title`)}</h3>
      <Answer answer={validation} help={t(`${ns}:conselho.validation.help`)}>
        {(rows) => {
          const find = (h: Headline) =>
            h.statistic
              ? rows.find((r) => r.check === h.check && r.statistic === h.statistic)
              : rows.find((r) => r.check === h.check);
          const rest = rows.filter(
            (r) => !headlines.some((h) => r.check === h.check && (h.statistic === undefined || r.statistic === h.statistic)),
          );
          return (
            <div className="space-y-3">
              <dl className="grid gap-x-4 gap-y-2 text-xs sm:grid-cols-2 lg:grid-cols-4">
                {headlines.map((h) => {
                  const row = find(h);
                  const passed = row != null && (row.statistic === 'passed' || row.value === 1);
                  return (
                    <div key={h.key} data-validation={h.key} data-value={row?.value ?? ''}>
                      <dt className="text-muted-foreground">{t(`${ns}:conselho.validation.${h.key}`)}</dt>
                      <dd className="tabular-nums text-foreground">
                        {row && row.value !== null ? (
                          h.kind === 'passfail' ? (
                            t(`${ns}:conselho.validation.${passed ? 'passed' : 'failed'}`)
                          ) : (
                            <>
                              {(UNSIGNED.has(h.key) ? number : signed).format(row.value)}
                              <span className="ml-1 text-muted-foreground">
                                ({t(`${ns}:conselho.validation.n`, { n: integer.format(row.n) })})
                              </span>
                            </>
                          )
                        ) : (
                          <span className="text-muted-foreground">{t(`${ns}:conselho.validation.notComputed`)}</span>
                        )}
                      </dd>
                      <dd className="text-[11px] text-muted-foreground">{t(`${ns}:conselho.validation.${h.key}Help`)}</dd>
                    </div>
                  );
                })}
              </dl>
              {rest.length > 0 ? (
                <details className="text-[11px] text-muted-foreground">
                  <summary className="cursor-pointer">{t(`${ns}:conselho.validation.allRows`, { count: rest.length })}</summary>
                  <ul className="mt-1 space-y-0.5" data-testid="validation-rows">
                    {rest.map((r) => (
                      <li key={`${r.check}/${r.statistic}`} className="tabular-nums">
                        {r.check}/{r.statistic}:{' '}
                        {r.value !== null ? signed.format(r.value) : t(`${ns}:conselho.validation.notComputed`)}
                        {r.lower !== null && r.upper !== null ? ` [${signed.format(r.lower)}, ${signed.format(r.upper)}]` : ''}
                        {r.n ? ` (n=${integer.format(r.n)})` : ''}
                        {r.note ? ` — ${r.note}` : ''}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </div>
          );
        }}
      </Answer>
    </div>
  );
}
